import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ReactNodeDialog } from "../utils/dialogUtlities"
import Card from "./Card"
import { CustomValidator } from "./CustomValidator"
import DefaultButton from "./DefaultButton"
import { Col, Row } from "./Grid"
import PasswordInput from "./PasswordInput"
import ReactForm from "./ReactForm"
import PropTypes from 'prop-types';

const PasswordResetImpl = forwardRef(({ value, requireCurrent, onChange }, ref) => {
    const { t } = useTranslation();
    const currentRef = useRef();
    const passwordRef = useRef();

    useImperativeHandle(ref, () => ({
        focus: () => requireCurrent ? currentRef.current?.focus() : passwordRef.current?.focus()
    }));

    const handleUpdate = useCallback(delta => {
        onChange(delta)
    }, [onChange]);

    return <>
        { requireCurrent && <Row>
            <Col>
                <PasswordInput ref={currentRef} placeholder={t('currentPassword')} required live value={value?.currentPassword} onChange={currentPassword => handleUpdate({ currentPassword })}></PasswordInput>
            </Col>
        </Row> }
        <Row>
            <Col>
                <PasswordInput ref={passwordRef} placeholder={t('password')} required live value={value?.password} onChange={password => handleUpdate({ password })}></PasswordInput>
            </Col>
        </Row>
        <Row>
            <Col>
                <PasswordInput placeholder={t('password')} required live value={value?.password2} onChange={password2 => handleUpdate({ password2 })}></PasswordInput>
                <CustomValidator value={value} live={true} onValidate={value => value.password?.length > 0 && value.password2?.length > 0 && value.password !== value.password2 ? t('passwordsDoNotMatch') : false}></CustomValidator>
            </Col>
        </Row>
    </>
});
PasswordResetImpl.propTypes = {
    /**
     * An object holding the password values
     */
    value: PropTypes.object.isRequired,
    /**
     * Set to true to prompt for the current password in addition to the new password
     */
    requireCurrent: PropTypes.bool,
    /**
     * Function called to update the value object - argument is a delta object
     */
    onChange: PropTypes.func.isRequired
}

export const PasswordResetCard = ({ title, requireCurrent, onSubmit, onClear }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState({});
    const formRef = useRef();
    const handleSubmit = useCallback(() => {
        if (formRef.current?.checkValidity()) {
            onSubmit(value);
        }
    }, [value, onSubmit]);
    const passwordFooterControls = <Row>
        <Col></Col>
        <Col xs='auto' className="pr-2"><DefaultButton kind='clear' onClick={() => { setValue({}); onClear?.() }}></DefaultButton></Col>
        <Col xs='auto'><DefaultButton kind="apply" onClick={handleSubmit}></DefaultButton></Col>
    </Row>

    return <Card title={title ?? t('resetPassword')} footerControls={passwordFooterControls}>
        <ReactForm ref={formRef}>
            <PasswordResetImpl value={value} requireCurrent={requireCurrent} onChange={delta => setValue(value => ({ ...value, ...delta }))}></PasswordResetImpl>
        </ReactForm>
    </Card>
}
PasswordResetCard.propTypes = {
    /**
     * The title of the password reset card
     */
    title: PropTypes.string,
    /**
     * Set to true to prompt for the current password in addition to the new password
     */
    requireCurrent: PropTypes.bool,
    /**
     * Function called with the reset object when the user clicks to submit the change request
     */
    onSubmit: PropTypes.bool,
    /**
     * Function called when the user clears the reset control
     */
    onClear: PropTypes.bool
}

export const PasswordResetDialog = ({ show, requireCurrent, onSubmit, onClose }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState({});
    const formRef = useRef();

    const handleSubmit = useCallback(() => {
        if (formRef.current?.checkValidity()) {
            onSubmit(value);
            setValue({});
        }
    }, [onSubmit, value])

    return <ReactNodeDialog title={t('passwordReset')} size="sm" showModal={show} onSave={handleSubmit} onClose={onClose}>
        <ReactForm ref={formRef}>
            <PasswordResetImpl value={value} requireCurrent={requireCurrent} onChange={delta => setValue(value => ({ ...value, ...delta }))}></PasswordResetImpl>
        </ReactForm>
    </ReactNodeDialog>
}
PasswordResetDialog.propTypes = {
    show: PropTypes.bool,
    /**
     * Set to true to prompt for the current password in addition to the new password
     */
    requireCurrent: PropTypes.bool,
    /**
     * Function called with the reset object when the user clicks to submit the change request
     */
    onSubmit: PropTypes.bool,
    /**
     * Function called when the user clears the reset control
     */
    onClear: PropTypes.bool
}

export const PasswordResetCtrl = forwardRef(({ value, requireCurrent, onChange }, ref) => {
    return <PasswordResetImpl ref={ref} value={value} onChange={onChange} requireCurrent={requireCurrent}></PasswordResetImpl>
});
PasswordResetCtrl.propTypes = {
    /**
     * An object holding the password values
     */
    value: PropTypes.object.isRequired,
    /**
     * Set to true to prompt for the current password in addition to the new password
     */
    requireCurrent: PropTypes.bool,
    /**
     * Function called to update the value object - argument is a delta object
     */
    onChange: PropTypes.func.isRequired
}